import { ProductBundleOption, ProductLink } from '../../../models';
import {
  LocalChapter,
  ProfileModel,
  RedemptionProfile,
} from '../ProfileUpdateModal/models';
import { Chapter } from './ChapterSelectionBlock';

const MAX_SUGGESTED_CHAPTERS = 3;
const CHAPTER_TITLE_KEYWORD = 'chapter';

export enum ExperienceType {
  NonMar = 0,
  Mar = 1,
}

export const initialProfileValues: ProfileModel = {
  phoneNumber: {
    countryId: null,
    extension: '',
    number: '',
  },
  address: { country: '', state: '' },
};

export const isMarExperience = (experienceType: number) =>
  experienceType === ExperienceType.Mar;

export const isChapterBundleOptionTitle = (title: string = '') =>
  title.toLowerCase().includes(CHAPTER_TITLE_KEYWORD);

export const getInitialChapterSelection = (
  bundleOptions: ProductBundleOption[] = [],
): ProductLink | Chapter | null =>
  bundleOptions
    .find((option) => isChapterBundleOptionTitle(option.title))
    ?.productLinks?.find((productLink) => productLink.ownsProduct) ?? null;

export const findPrimaryAddress = (profile: RedemptionProfile | null) =>
  profile?.addresses?.items?.find((address) => address.primary) ?? null;

export const getSuggestedChapters = (
  selectedChapter: ProductLink | Chapter | null,
  chaptersInBundle: Chapter[],
  localChapters: LocalChapter[],
) => {
  let preselectionList = chaptersInBundle;

  if (localChapters.length > 0) {
    preselectionList = preselectionList
      .filter((chapter) =>
        localChapters.some(
          (localChapter) => localChapter.chapterSKU === chapter.sku,
        ),
      )
      .sort((a, b) => {
        const aIndex = localChapters.findIndex(
          (localChapter) => localChapter.chapterSKU === a.sku,
        );
        const bIndex = localChapters.findIndex(
          (localChapter) => localChapter.chapterSKU === b.sku,
        );
        return aIndex - bIndex;
      });
  }

  return preselectionList.slice(0, MAX_SUGGESTED_CHAPTERS);
};

export const isBundleOptionSelectable = (
  bundleOption: ProductBundleOption,
): boolean => Boolean(bundleOption?.selectOption);

export const getSelectableBundleOptions = (
  bundleOptions: ProductBundleOption[] = [],
): ProductBundleOption[] => bundleOptions.filter(isBundleOptionSelectable);

export const getBundleOptionsWithSelectedChapter = (
  bundleOptions: ProductBundleOption[],
  chapterSelected: ProductLink | Chapter | null,
  selectionsBySku: Record<string, string> = {},
) =>
  bundleOptions.map((bundleOption) => {
    if (isChapterBundleOptionTitle(bundleOption.title)) {
      const clone = { ...bundleOption };
      clone.productLinks = [chapterSelected];
      return clone;
    }

    if (isBundleOptionSelectable(bundleOption)) {
      const selectedSku = selectionsBySku[String(bundleOption.id)];
      if (!selectedSku) {
        return bundleOption;
      }
      const matched = bundleOption.productLinks.find(
        (link) => link.sku === selectedSku,
      );
      if (!matched) {
        return bundleOption;
      }
      return { ...bundleOption, productLinks: [matched] };
    }

    return bundleOption;
  });
